/**
 * crmFunnel/filters.ts
 *
 * Filter logic for the CRM Funnel module.
 *
 * Only Meta paid leads qualify:
 *   platform === "meta" AND (utmMedium === "paid_social" OR source IN ["paid_meta", "meta_ads"])
 *
 * Additional dimension filters: dateRange, campaign, adset, ad, source, platform.
 * Date comparison uses string comparison (YYYY-MM-DD is lexicographically sortable).
 */

import type { CrmLeadRow, CrmFunnelFilters, CrmFunnelFilterOptions } from "./types";

// ─────────────────────────────────────────────
// PAID META GUARD
// ─────────────────────────────────────────────

const PAID_META_SOURCES  = new Set(["paid_meta", "meta_ads"]);
const PAID_META_MEDIUMS  = new Set(["paid_social"]);

/**
 * Returns true only if the row is a paid Meta lead.
 * This is the core eligibility check for this module.
 */
function isPaidMetaLead(row: CrmLeadRow): boolean {
  return (
    row.platform === "meta" &&
    (PAID_META_MEDIUMS.has(row.utmMedium) || PAID_META_SOURCES.has(row.source))
  );
}

// ─────────────────────────────────────────────
// APPLY FILTERS
// ─────────────────────────────────────────────

/**
 * Filters CRM lead rows by all active filter dimensions.
 * Always enforces the paid Meta guard first.
 */
export function applyFilters(
  rows: CrmLeadRow[],
  filters: Partial<CrmFunnelFilters> & { dateRange: CrmFunnelFilters["dateRange"] }
): CrmLeadRow[] {
  const { dateRange, metaCampaignIds, metaAdsetIds, metaAdIds, sources, platforms } = filters;

  return rows.filter(row => {
    // Paid Meta guard — always enforced
    if (!isPaidMetaLead(row)) return false;

    // Date range
    if (row.createdAt < dateRange.start || row.createdAt > dateRange.end) return false;

    // Campaign filter
    if (metaCampaignIds && metaCampaignIds.length > 0) {
      if (!metaCampaignIds.includes(row.metaCampaignId)) return false;
    }

    // Adset filter
    if (metaAdsetIds && metaAdsetIds.length > 0) {
      if (!metaAdsetIds.includes(row.metaAdsetId)) return false;
    }

    // Ad filter
    if (metaAdIds && metaAdIds.length > 0) {
      if (!metaAdIds.includes(row.metaAdId)) return false;
    }

    // Source filter
    if (sources && sources.length > 0) {
      if (!sources.includes(row.source)) return false;
    }

    // Platform filter (future-proofing — currently always "meta")
    if (platforms && platforms.length > 0) {
      if (!platforms.includes(row.platform)) return false;
    }

    return true;
  });
}

// ─────────────────────────────────────────────
// PREVIOUS PERIOD
// ─────────────────────────────────────────────

/**
 * Returns rows that fall within the previous period date range.
 * Previous period = same duration, immediately before the current start date.
 * e.g. Jan 2–23 (22 days) → previous = Dec 11–Jan 1 (22 days)
 */
export function getPreviousPeriodRows(
  rows: CrmLeadRow[],
  dateRange: CrmFunnelFilters["dateRange"]
): CrmLeadRow[] {
  const startMs    = new Date(dateRange.start + "T00:00:00Z").getTime();
  const endMs      = new Date(dateRange.end   + "T00:00:00Z").getTime();
  const durationMs = endMs - startMs + 86_400_000; // inclusive end

  const prevEndMs   = startMs - 86_400_000;
  const prevStartMs = prevEndMs - durationMs + 86_400_000;

  const prevStart = new Date(prevStartMs).toISOString().slice(0, 10);
  const prevEnd   = new Date(prevEndMs).toISOString().slice(0, 10);

  return rows.filter(
    row => row.createdAt >= prevStart && row.createdAt <= prevEnd
  );
}

// ─────────────────────────────────────────────
// BUILD FILTER OPTIONS
// ─────────────────────────────────────────────

/**
 * Derives unique dropdown options from the full raw dataset.
 * Applies only the paid Meta guard — not date or other filters.
 * This ensures dropdowns always show all available dimensions.
 */
export function buildFilterOptions(rows: CrmLeadRow[]): CrmFunnelFilterOptions {
  const campaignMap = new Map<string, string>();
  const adsetMap    = new Map<string, string>();
  const adMap       = new Map<string, string>();
  const sourceSet   = new Set<string>();

  for (const row of rows) {
    if (!isPaidMetaLead(row)) continue;
    campaignMap.set(row.metaCampaignId, row.metaCampaignName);
    adsetMap.set(row.metaAdsetId,       row.metaAdsetName);
    adMap.set(row.metaAdId,             row.metaAdName);
    sourceSet.add(row.source);
  }

  return {
    campaigns: Array.from(campaignMap.entries()).map(([id, name]) => ({ id, name })),
    adsets:    Array.from(adsetMap.entries()).map(([id, name]) => ({ id, name })),
    ads:       Array.from(adMap.entries()).map(([id, name]) => ({ id, name })),
    sources:   Array.from(sourceSet).sort(),
  };
}
